/**
 * A stable id for this machine, minted once and kept.
 *
 * Two unrelated things need to know one device from another, and they need to agree on the answer.
 * A judge's access code binds to the first device that presents it, so a code read over somebody's
 * shoulder is refused on the second (`access_codes.bound_device_id`). And a registration desk that
 * has been told which desk it is (D86) records the machine that issued each number, so two machines
 * accidentally set to the same desk can be *noticed* rather than silently interleaving their
 * numbers.
 *
 * It was written out in the judge screen first, where it was the only caller. Copied to a second
 * caller it would have become two keys, and two keys is one machine with two identities — the judge
 * screen's binding and the desk's warning would then be about different things while reading as if
 * they were about the same one.
 *
 * Opaque and local: a ULID with nothing in it, never sent anywhere but this platform, and worth no
 * more to somebody who reads it than "this is a browser".
 */

import { ulid as mintUlid } from "@powwow/domain";

const KEY = "pw.device";

export function deviceId(): string {
  let id: string | null = null;
  try {
    id = localStorage.getItem(KEY);
  } catch {
    // A kiosk profile with storage locked down gets a fresh id per call. Both callers degrade
    // sensibly: a judge is asked to sign in again, and a desk warning is not raised. Neither is
    // worth failing a registration over.
    return mintUlid(Date.now());
  }
  if (id) return id;

  const minted = mintUlid(Date.now());
  try {
    localStorage.setItem(KEY, minted);
  } catch {
    /* As above. */
  }
  return minted;
}
